// Pull a patient's chart data + documents so a physician can fold them into a
// dictation note. All reads + the note regeneration go over the FastAPI proxy
// to the tenant's own APIs — nothing runs locally. Logic for active meds +
// problem chronicity mirrors the chart (onechart_v2.html) so the two stay in sync.
import { api } from "@/lib/api";
import { ensureApiKey, ensureNpi, generateSoap, getSoap } from "@/lib/dictation";

const arr = (x) => (Array.isArray(x) ? x : []);
const s = (v) => (v == null ? "" : String(v).trim());
const num = (x) => { const n = Number(x); return Number.isFinite(n) ? String(n) : s(x); };
const ts = (v) => { if (!v) return 0; const d = new Date(v); return isNaN(d) ? 0 : d.getTime(); };
const fmtDate = (v) => { const d = v ? new Date(v) : null; return d && !isNaN(d) ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : s(v); };
const prettyKey = (k) => s(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
export const prettyType = (t) => s(t).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Document";

// Loose check: is a chart item already present in the current note text? Used to
// grey items that are already in the note so staging/counts only reflect genuinely
// new additions. lowText must be the note draft lower-cased.
export function itemInNote(it, lowText) {
  if (!lowText) return false;
  const probe = String(it?.name || it?.code || it?.text || "").toLowerCase().replace(/\s+/g, " ").trim().slice(0, 40);
  return probe.length >= 3 && lowText.includes(probe);
}
const DAY = 86400000;

// ── ICD-10 chronicity (ported from onechart_v2.html, Dan's Sep 15 ruling) ──
const ICD_CHRONIC = [/^E0[89]/, /^E1[0-3]/, /^E03/, /^E05/, /^E78/, /^E66/, /^I1[0-5]/, /^I2[0-5]/, /^I48/, /^I50/, /^I6[3-9]/, /^I7[0-9]/, /^I87/, /^J4[0-7]/, /^J96/, /^N18/, /^C[0-9]/, /^D0/, /^F2[0-9]/, /^F3[0-9]/, /^F41/, /^G20/, /^G3[0-5]/, /^M0[5-6]/, /^M1[5-9]/, /^M8[01]/, /^K57/, /^K21/, /^K7[0-4]/, /^E55/, /^G47/];
const ICD_ACUTE = [/^J0[0-6]/, /^J1[0-8]/, /^J2[0-2]/, /^N39/, /^N3[0-4]/, /^L0[0-8]/, /^A0[0-9]/, /^B3[0-4]/, /^S[0-9]/, /^T[0-9]/, /^R[0-9]/, /^K3[5-8]/, /^H6[0-6]/, /^J3[0-9]/, /^O[0-9]/, /^Z[0-9]/];
const icdKey = (code) => s(code).toUpperCase().replace(/[^A-Z0-9]/g, "");
function icdChronic(code) {
  const c = icdKey(code);
  if (!c) return null;
  if (ICD_CHRONIC.some((r) => r.test(c))) return true;
  if (ICD_ACUTE.some((r) => r.test(c))) return false;
  return null;
}

function vitalLines(v) {
  const parts = [];
  if (v.systolic_bp != null && v.diastolic_bp != null) parts.push(`BP ${num(v.systolic_bp)}/${num(v.diastolic_bp)} mmHg`);
  if (v.heart_rate != null) parts.push(`HR ${num(v.heart_rate)} bpm`);
  if (v.respiratory_rate != null) parts.push(`RR ${num(v.respiratory_rate)}`);
  if (v.temperature != null) parts.push(`Temp ${num(v.temperature)}`);
  if (v.spo2 != null) parts.push(`SpO2 ${num(v.spo2)}%`);
  if (v.weight_lbs != null || v.weight != null) parts.push(`Wt ${num(v.weight_lbs ?? v.weight)} lb`);
  if (v.height != null) parts.push(`Ht ${num(v.height)}`);
  if (v.bmi != null) parts.push(`BMI ${num(v.bmi)}`);
  if (s(v.pain_level)) parts.push(`Pain ${s(v.pain_level)}`);
  if (!parts.length) return [];
  return [{ key: "vit-all", cat: "Vitals", text: parts.join(" · "), date: fmtDate(v.recorded_at) }];
}

function buildMeds(d) {
  const today = Date.now();
  const feed = [
    ...arr(d.active_medications).map((m) => ({ name: s(m.medication_name) || s(m.name), dosage: s(m.dosage), sig: s(m.sig_line) || s(m.sig), start: m.start_date, end: m.end_date, status: s(m.status) })),
    ...arr(d.erx).map((m) => ({ name: s(m.drug_name) || s(m.medication_name), dosage: s(m.dosage), sig: s(m.drug_sig) || s(m.sig), start: m.rx_start_date || m.start_date, end: m.effective_rx_end_date || m.rx_end_date || m.end_date, status: s(m.status) })),
  ].filter((m) => m.name);
  const groups = {};
  feed.forEach((m) => { const k = m.name.toUpperCase(); (groups[k] = groups[k] || []).push(m); });
  const out = [];
  Object.entries(groups).forEach(([k, g]) => {
    const withEnd = g.map((m) => {
      const startT = ts(m.start);
      const directEnd = ts(m.end);
      const effEnd = directEnd || (startT ? startT + 90 * DAY : 0);
      return { ...m, startT, directEnd, effEnd };
    });
    withEnd.sort((a, b) => b.effEnd - a.effEnd); // keep the row with the highest direct/deduced end
    const m = withEnd[0];
    if (/inactive|discontinu|stopped|cancel|resolv/i.test(m.status)) return;
    if (!(m.effEnd && m.effEnd >= today)) return; // active = end (or start+90d) in the future
    const endLabel = m.directEnd ? fmtDate(m.end) : `${fmtDate(new Date(m.effEnd))} (est.)`;
    const head = [prettyType(k), m.dosage].filter(Boolean).join(" ");
    out.push({
      key: `med-${k}`, cat: "Medication", name: prettyType(k), dosage: m.dosage, sig: m.sig,
      start: fmtDate(m.start), end: endLabel,
      text: `${head}${m.sig ? ` — ${m.sig}` : ""} (started ${fmtDate(m.start) || "unknown"}, ends ${endLabel})`,
    });
  });
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

function buildProblems(d) {
  const src = arr(d.problems).length ? arr(d.problems) : arr(d.top_problems);
  const groups = {}; const order = [];
  src.forEach((p) => {
    const k = icdKey(p.icd10_code) || `desc:${s(p.icd10_desc || p.description).toLowerCase()}`;
    if (!groups[k]) { groups[k] = []; order.push(k); }
    groups[k].push(p);
  });
  const chronic = []; const acute = [];
  const pdate = (p) => ts(p.last_encounter_date) || ts(p.updated_on) || ts(p.onset_date);
  order.forEach((k) => {
    const g = groups[k].slice().sort((a, b) => pdate(b) - pdate(a));
    const p = g[0];
    const stRaw = s(p.status);
    if (/resolv|inactive/i.test(stRaw)) return;
    const active = /active|chronic/i.test(stRaw) || stRaw === "";
    if (!active) return;
    const nature = (g.find((x) => /chronic|acute/i.test(x.nature || "")) || {}).nature || "";
    let chron = null;
    if (/chronic/i.test(nature)) chron = true;
    else if (/acute/i.test(nature)) chron = false;
    else if (g.some((x) => /^chronic$/i.test(s(x.status)))) chron = true;
    else chron = icdChronic(p.icd10_code);
    const onsets = g.map((x) => ts(x.onset_date)).filter(Boolean).sort((a, b) => a - b);
    const onset = onsets[0] ? fmtDate(new Date(onsets[0])) : "";
    const last = pdate(p) ? fmtDate(new Date(pdate(p))) : "";
    const code = s(p.icd10_code);
    const desc = s(p.icd10_desc) || s(p.description) || "Unnamed problem";
    const dates = [onset ? `onset ${onset}` : "", last ? `last eval ${last}` : ""].filter(Boolean).join(" · ");
    const item = { key: `prob-${k}`, cat: "Problem", code, desc, onset, last, text: `${code ? `${code} — ` : ""}${desc}${dates ? ` (${dates})` : ""}` };
    (chron === true ? chronic : acute).push(item);
  });
  return { chronic, acute };
}

function buildAllergies(d) {
  const src = [...arr(d.recent_allergies), ...arr(d.history_allergies)];
  const seen = new Map();
  src.forEach((a) => {
    const sub = s(a.substance) || s(a.name) || s(a.allergen);
    if (!sub) return;
    const k = sub.toUpperCase();
    const text = [sub, [s(a.severity), s(a.reaction)].filter(Boolean).join(", ")].filter(Boolean).join(" — ");
    const score = (s(a.severity) ? 1 : 0) + (s(a.reaction) && !/unknown/i.test(a.reaction) ? 1 : 0);
    const cur = seen.get(k);
    if (!cur || score > cur.score) seen.set(k, { key: `alg-${k}`, cat: "Allergy", text, score });
  });
  return [...seen.values()].map(({ score, ...x }) => x);
}

function buildLabs(d) {
  const groups = {};
  arr(d.elabs).forEach((l) => {
    const name = s(l.ai_study_name) || s(l.result_code_description);
    if (!name) return;
    const k = `${name.toUpperCase()}|${s(l.body_part).toUpperCase()}`;
    const t = ts(l.result_date);
    if (!groups[k] || t >= groups[k]._t) groups[k] = { ...l, _t: t, _name: name };
  });
  return Object.values(groups).map((l, i) => ({
    key: `lab-${i}`, cat: "Lab/imaging",
    text: ([l._name, s(l.body_part) ? `(${s(l.body_part)})` : ""].filter(Boolean).join(" ")
      + (s(l.result_value) ? `: ${s(l.result_value)} ${s(l.result_uom)}`.trim() : "")
      + (s(l.abnormal_flag) ? ` [${s(l.abnormal_flag)}]` : "")).trim(),
    date: fmtDate(l.result_date),
  })).filter((x) => x.text).sort((a, b) => a.text.localeCompare(b.text));
}

export async function fetchChartBundle(uuid) {
  const { data: d } = await api.get(`/api/ehibridge/import/chart-by-id/${encodeURIComponent(uuid)}`);
  const allergies = buildAllergies(d);
  const meds = buildMeds(d);
  const { chronic, acute } = buildProblems(d);
  const vObj = d.recent_vitals && typeof d.recent_vitals === "object" && !Array.isArray(d.recent_vitals) ? d.recent_vitals : (arr(d.all_vitals)[0] || null);
  const vitals = vObj ? vitalLines(vObj) : [];
  const labs = buildLabs(d);
  return { allergies, meds, problemsChronic: chronic, problemsAcute: acute, vitals, labs };
}

const DOC_SKIP = new Set(["patient_id", "patient_name", "date_of_birth", "extraction_confidence", "id", "patient_dob"]);
function readable(obj, depth, out) {
  if (depth > 2 || out.length > 60) return out;
  if (Array.isArray(obj)) { obj.slice(0, 8).forEach((v) => readable(v, depth + 1, out)); return out; }
  if (obj && typeof obj === "object") {
    for (const [k, v] of Object.entries(obj)) {
      if (DOC_SKIP.has(k) || v == null || v === "") continue;
      if (typeof v === "object") { out.push(`${prettyKey(k)}:`); readable(v, depth + 1, out); }
      else out.push(`${prettyKey(k)}: ${v}`);
    }
  } else if (obj != null && obj !== "") out.push(String(obj));
  return out;
}
function docSummary(a) {
  const txt = readable(a.extraction || {}, 0, []).join("\n").trim();
  return txt.length > 1600 ? `${txt.slice(0, 1600)}…` : txt;
}
const DOC_DATE_KEYS = ["encounter_date", "date_of_service", "visit_date", "exam_date", "study_date", "collection_date", "collected_at", "resulted_at", "result_date", "service_date", "document_date"];
function docDate(a) {
  const ext = a.extraction || {};
  for (const k of DOC_DATE_KEYS) { if (s(ext[k])) return fmtDate(ext[k]); }
  return fmtDate(a.created_at);
}

export async function fetchPatientDocs(clientId, mrn, uuid) {
  if (!clientId) return [];
  const { data } = await api.get("/api/edi/documents/analyses", { params: { client_id: clientId, skip: 0, limit: 300 } });
  const all = data.analyses || data.results || data.items || [];
  return all
    .filter((a) => {
      const p = (a.edi_client_match || {}).patient || {};
      return (mrn && p.mrn === mrn) || (uuid && a.linked_chart_uuid === uuid);
    })
    .map((a) => ({ key: `doc-${a.id}`, cat: "Document", id: a.id, type: a.document_type || "document", name: a.filename || a.file_name || "Document", date: docDate(a), summary: docSummary(a) }))
    .filter((dd) => dd.summary);
}

// Regenerate the visit note on the tenant's OWN SOAP engine — the same flow the
// original dictation uses (transcript → /dictation/soap-note → poll). The picks
// are appended to the note text as additional context so the note is rebuilt
// cleanly rather than having raw lines pasted in. No local/LLM calls here.
const nz = (x) => {
  if (x == null) return "";
  if (typeof x === "object") {
    // Some engine fields (e.g. call_summary) can come back as an object — pull a
    // readable string if one is there, otherwise skip rather than print "[object Object]".
    const s2 = x.summary ?? x.text ?? x.content ?? x.value ?? x.description ?? "";
    return typeof s2 === "string" ? (/^not documented/i.test(s2.trim()) ? "" : s2.trim()) : "";
  }
  const t = String(x).trim();
  return /^not documented/i.test(t) ? "" : t;
};
const listText = (v, fmt) => (Array.isArray(v) ? v.map(fmt).map(nz).filter(Boolean).map((l) => `- ${l}`).join("\n") : "");

// Remove any stray "[object Object]" the engine (or an earlier buggy rebuild) baked into
// note text — including a "Label:" header left orphaned above it — and tidy blank lines.
export const stripObjObj = (t) => String(t || "")
  .replace(/\n{0,2}[^\n:]{1,60}:\s*\n+\[object Object\][ \t]*/g, "")
  .replace(/\[object Object\]/g, "")
  .replace(/\n{3,}/g, "\n\n")
  .trim();

// Assemble the COMPLETE note from the engine result, keeping every section it
// returns (not just S/O/A/P). Prefers a full formatted-note string if present.
function assembleFullNote(r) {
  r = r || {};
  const full = nz(r.formatted_note) || nz(r.full_note) || nz(r.visit_note);
  if (full) return stripObjObj(full);

  const en = r.encounter_note || {};
  const soap = r.soap_note || {};
  const out = [];
  const add = (label, text) => { const t = nz(text); if (t) out.push(`${label}:\n${t}`); };

  add("Chief complaint", en.chief_complaint || soap.subjective);
  add("History of present illness", en.history_of_present_illness);
  add("Review of systems", en.review_of_systems);
  add("Vitals", en.vital_signs);
  add("Physical examination", en.physical_exam);

  const dr = en.diagnostic_results;
  if (dr && typeof dr === "object" && !Array.isArray(dr)) {
    const labs = listText(dr.laboratory_results, (x) => `${nz(x.name || x.test || x.description)}${nz(x.value) ? `: ${nz(x.value)} ${nz(x.unit)}`.trim() : ""}${nz(x.flag) ? ` [${nz(x.flag)}]` : ""}`);
    const rad = listText(dr.radiology_reports, (x) => `${nz(x.name || x.study || x.description)}${nz(x.impression) ? `: ${nz(x.impression)}` : ""}`);
    const oth = listText(dr.other_results, (x) => nz(x.description || x.name || x));
    add("Diagnostic results", [labs, rad, oth].filter(Boolean).join("\n"));
  } else {
    add("Diagnostic results", soap.objective);
  }

  const dxList = listText(r.diagnoses, (x) => `${nz(x.description || x.name || x.icd10_desc)}${nz(x.icd10_code || x.code) ? ` (${nz(x.icd10_code || x.code)})` : ""}`);
  add("Assessment", [nz(en.assessment || soap.assessment), dxList].filter(Boolean).join("\n"));

  const planList = listText(r.plans, (x) => nz(x.description || x.text));
  add("Plan", [nz(en.plan || soap.plan), planList].filter(Boolean).join("\n"));

  add("Current medications", listText(r.medications, (x) => [nz(x.name || x.medication_name || x.legal_medication_name), nz(x.dosage), nz(x.frequency)].filter(Boolean).join(" ")));

  const mr = r.medication_reconciliation || {};
  add("Prescriptions", listText(mr.new_prescriptions, (x) => [nz(x.legal_medication_name || x.name), nz(x.dosage || x.sig)].filter(Boolean).join(" — ")));

  add("Orders", listText(r.orders, (x) => nz(x.description || x.name || x.test)));

  add("Patient education & follow-up", listText(r.patient_engagement_plan, (x) => [nz(x.event_day), nz(x.event_type), nz(x.text_message || x.purpose)].filter(Boolean).join(" — ")));

  add("Summary", r.call_summary);

  if (!out.length) return stripObjObj(nz(r.readable_soap_note) || "");
  return stripObjObj(out.join("\n\n"));
}

export async function reformatNote(baseText, additions, meta = {}) {
  const sid = meta.sessionId || undefined;
  const transcript = meta.labeled
    ? `${s(baseText)}\n\n${arr(additions).join("\n")}`
    : `${s(baseText)}\n\nAdditional information from the patient's chart to incorporate into the note:\n${arr(additions).map((a) => `- ${a}`).join("\n")}`;
  await ensureApiKey().catch(() => {});
  if (meta.npi) await ensureNpi({ npi_number: meta.npi }).catch(() => {});
  const r = await generateSoap({
    session_id: sid, dictation_session_id: sid, ...(meta.patientId ? { patient_id: meta.patientId } : {}),
    client_id: meta.clientId, npi_number: meta.npi || undefined,
    specialty: meta.specialty || undefined, transcript,
  });
  const id = r.soap_note_id || r.soap_id || r.id;
  if (!id) throw new Error(r?.detail || "The note engine did not start.");
  const t0 = Date.now();
  while (Date.now() - t0 < 180000) {
    await new Promise((res) => setTimeout(res, 3000));
    const d = await getSoap(id);
    const st = String(d.status || "").toLowerCase();
    if (st === "completed") return assembleFullNote(d.result);
    if (st === "failed" || st === "error") throw new Error("The note engine couldn't format the note.");
  }
  throw new Error("Timed out generating the note.");
}

